/**
 * AssignRoleDialog
 *
 * Modal dialog for assigning or editing a user's role and status. Supports enabling/disabling
 * a user with a confirmation path, selecting a role, and choosing participating departments.
 * Uses Formik + Yup for form state/validation and integrates with roles/search dashboard APIs.
 */
import React from "react";
import PropTypes from "prop-types";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Box,
  Chip,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CommonSelect from "../../common/CommonSelect";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import "./AssignRoleDialog.css";

import { updateEmployeeApplicationStatus } from "../../../api/roles";

const TextInputComponent = ({
  type = "text",
  fieldName,
  label,
  value,
  disabled = false,
  onChange,
}) => {
  return (
    <div className="form-text-input-component">
      {label && <InputLabel htmlFor={fieldName}>{label}</InputLabel>}
      <TextField
        id={fieldName}
        name={fieldName}
        type={type}
        value={value}
        disabled={disabled}
        fullWidth
        variant="outlined"
        slotProps={{
          input: {
            sx: {
              backgroundColor: disabled ? "#e9e9e8" : "#fff",
              borderRadius: "8px",
            },
          },
        }}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </div>
  );
};

TextInputComponent.propTypes = {
  type: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

// Helper: Get department chip background color
const getDepartmentChipColor = (deptId, departmentsList) => {
  const dept = departmentsList.find((d) => d.id === deptId);
  return dept?.color;
};

// Component: Department Chip (extracted to reduce nesting)
const DepartmentChip = ({ dept, departmentsList, onDelete }) => {
  const chipColor = getDepartmentChipColor(dept.id, departmentsList);
  return (
    <Chip
      key={dept.id}
      label={dept.text}
      onDelete={onDelete}
      sx={{
        backgroundColor: chipColor,
      }}
    />
  );
};

DepartmentChip.propTypes = {
  dept: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.number,
    ]).isRequired,
    departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string,
  }).isRequired,
  departmentsList: PropTypes.array,
  onDelete: PropTypes.func,
};

// Helper: Calculate initial selected roles
const calculateInitialSelectedRoles = (
  user,
  type,
  roles,
  getDisplayRoleName,
) => {
  if (
    type !== "edit" ||
    !Array.isArray(user.roles) ||
    user.roles.length === 0
  ) {
    return [];
  }
  return user.roles
    .map((r) => {
      const idVal = r?.roleId ?? r?.id;
      if (idVal != null) return String(idVal);
      const displayName = r?.roleName || r?.name;
      const match = Array.isArray(roles)
        ? roles.find((rr) => getDisplayRoleName(rr.name) === displayName)
        : null;
      return match ? String(match.id) : null;
    })
    .filter(Boolean);
};

const AssignRoleDialog = ({
  open,
  onClose,
  onSubmit,
  user,
  roles,
  type,
  onRefresh,
}) => {
  const departmentsList = [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getDisplayRoleName = (roleName) => roleName;

  if (!user) return null;

  const initialStatus = (() => {
    const raw = user?.status;
    if (raw === 1) return "Enable";
    const s = typeof raw === "string" ? raw.toLowerCase() : "";
    if (s === "active" || s === "enable") return "Enable";
    if (s === "inactive" || s === "disable") return "Disable";
    return "Enable";
  })();

  const initialValues = {
    username: user.employeeFullName || "",
    email: user.emailId || "",
    status: initialStatus,

    selectedRoles: calculateInitialSelectedRoles(
      user,
      type,
      roles,
      getDisplayRoleName,
    ),
    department: user?.departmentName || "",
    participatingDepartments: user?.departmentName
      ? [{ id: user.departmentId, text: user.departmentName }]
      : [],
  };

  const subDepartmentList = [
    { color: "#FCE8D2", text: "Sales", id: [1, 11, 21, 31, 41, 51] },
    { color: "#D6ECD2", text: "Marketing", id: [2, 12, 22, 32, 42, 52] },
    { color: "#FFD9E7", text: "AI Team", id: [3, 13, 23, 33, 43, 53] },
    { color: "#DEC3A6", text: "Finance", id: [4, 14, 24, 34, 44, 54] },
    { color: "#DDEAFF", text: "Accounting", id: [5, 15, 25, 35, 45, 55] },
    { color: "#CAC3D6", text: "IT", id: [6, 16, 26, 36, 46, 56] },
    {
      color: "#C3E0DF",
      text: "Engineering & Transformation",
      id: [7, 17, 27, 37, 47, 57],
    },
    { color: "#BAD1B6", text: "PMO", id: [8, 18, 28, 38, 48, 58] },
    { color: "#FFFDD9", text: "Data", id: [9, 19, 29, 39, 49, 59] },
    {
      color: "#E0C3CE",
      text: "Technology and Data",
      id: [10, 20, 30, 40, 50, 60],
    },
  ];

  const mainDepartments = Array.isArray(departmentsList)
    ? departmentsList
        .filter(
          (departmentItem) =>
            departmentItem?.departmentName &&
            departmentItem.departmentId != null,
        )
        .map((departmentItem) => ({
          color:
            subDepartmentList.find((sub) =>
              Array.isArray(sub.id)
                ? sub.id.includes(departmentItem.departmentId)
                : false,
            )?.color || "#DDEAFF",
          text: departmentItem.departmentName,
          id: departmentItem.departmentId,
        }))
    : [];

  // Sort roles by id so dropdown shows L1, L2, L3... order
  const sortedRolesById = Array.isArray(roles)
    ? [...roles].sort((a, b) => {
        const ai = Number(a?.id ?? a?.roleId ?? 0);
        const bi = Number(b?.id ?? b?.roleId ?? 0);
        return ai - bi;
      })
    : [];

  const roleNameList = Array.isArray(sortedRolesById)
    ? sortedRolesById.map((r) => getDisplayRoleName(r.name))
    : [];

  const validationSchema = Yup.object().shape({
    status: Yup.string().required("Please select status"),
    participatingDepartments: Yup.array()
      .min(1, "Please select at least one department")
      .required("Please select a department"),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="assign-role-dialog"
    >
      <DialogTitle className="assign-role-header">
        <div className="header-wrapper">
          <div className="header-wrapper-text">
            {type === "assign" ? "Assign Role" : "Edit Role"}
          </div>
          <div className="image-wrapper">
            <CloseIcon className="close-icon" onClick={onClose} />
          </div>
        </div>
      </DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
        >
          {(formikProps) => (
            <AssignRoleFormContent
              {...formikProps}
              user={user}
              roles={roles}
              initialValues={initialValues}
              onSubmit={onSubmit}
              onClose={onClose}
              mainDepartments={mainDepartments}
              roleNameList={roleNameList}
              isMobile={isMobile}
              type={type}
            />
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

AssignRoleDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employeeFullName: PropTypes.string,
    emailId: PropTypes.string,
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    departmentName: PropTypes.string,
    departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roles: PropTypes.array,
  }),
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  ),
  type: PropTypes.string,
  onRefresh: PropTypes.func,
};

const AssignRoleFormContent = ({
  values,
  setFieldValue,
  isValid,
  dirty,
  resetForm,
  user,
  roles,
  initialValues,
  onSubmit,
  onClose,
  mainDepartments,
  roleNameList,
  isMobile,
  type,
}) => {
  const [showAllDepartments, setShowAllDepartments] = React.useState(false);
  const [statusAnchorEl, setStatusAnchorEl] = React.useState(null);
  const [roleAnchorEl, setRoleAnchorEl] = React.useState(null);

  const statusOpen = Boolean(statusAnchorEl);
  const roleOpen = Boolean(roleAnchorEl);

  const handleOpenStatus = (e) => setStatusAnchorEl(e.currentTarget);
  const handleCloseStatus = () => setStatusAnchorEl(null);

  const handleOpenRole = (e) => {
    if (values.status === "Disable") return;
    setRoleAnchorEl(e.currentTarget);
  };
  const handleCloseRole = () => setRoleAnchorEl(null);

  const getDisplayRoleName = (roleName) => roleName;

  const getRoleDisplayNames = (selectedRoleIds, rolesList) => {
    const result = [];
    if (!Array.isArray(rolesList)) return result;

    for (const id of selectedRoleIds) {
      const roleObj = rolesList.find((r) => String(r.id) === String(id));
      if (roleObj) {
        const displayName = getDisplayRoleName(roleObj.name);
        if (displayName) {
          result.push(displayName);
        }
      }
    }
    return result;
  };

  const getRoleDisplayText = (selectedRoleIds, rolesList) => {
    if (!Array.isArray(selectedRoleIds) || selectedRoleIds.length === 0) {
      return "Select Roles";
    }
    const names = getRoleDisplayNames(selectedRoleIds, rolesList);
    return names.length > 0 ? names.join(", ") : "Select Roles";
  };

  const getRoleBoxHoverStyles = (isDisabled) => ({
    boxShadow: isDisabled ? "0 0 0 1px transparent" : "0 0 0 1px #403C6F",
  });

  const getRoleBoxStyles = (isDisabled) => ({
    border: "1px solid #e8e8ee99",
    borderRadius: "5px",
    backgroundColor: isDisabled ? "#f2f2f2" : "#fff",
    fontSize: "16px",
    fontWeight: 600,
    color: "#403C6F",
    px: 2,
    py: 1,
    display: "flex",
    alignItems: "center",
    cursor: isDisabled ? "not-allowed" : "pointer",
    minHeight: "40px",
    opacity: isDisabled ? 0.6 : 1,
    boxShadow: "0 0 0 1px transparent",
    "&:hover": getRoleBoxHoverStyles(isDisabled),
  });

  const handleSave = async () => {
    try {
      let statusChanged = false;
      if (values.status === "Disable") {
        await updateEmployeeApplicationStatus(user.id, 2, "Disable");
        statusChanged = true;
      } else if (values.status === "Enable") {
        await updateEmployeeApplicationStatus(user.id, 2, "Enable");
        statusChanged = true;
      }

      const initialRoleIds = Array.isArray(initialValues.selectedRoles)
        ? initialValues.selectedRoles
            .map((id) => Number.parseInt(id, 10))
            .sort((a, b) => a - b)
        : [];
      const currentRoleIds = Array.isArray(values.selectedRoles)
        ? values.selectedRoles
            .map((id) => Number.parseInt(id, 10))
            .sort((a, b) => a - b)
        : [];
      const rolesChanged =
        initialRoleIds.length !== currentRoleIds.length ||
        initialRoleIds.some((id, idx) => id !== currentRoleIds[idx]);

      if ((statusChanged || rolesChanged) && typeof onSubmit === "function") {
        onSubmit({
          employeeId: user.id,
          roleId: currentRoleIds[0],
          allRoleIds: currentRoleIds,
          status: values.status,
          department: values.participatingDepartments,
        });
      }
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error saving role assignment:", error);
    }
  };

  const handleTypeToggleForStatus = (val) => {
    setFieldValue("status", val);
    if (val === "Disable") {
      setFieldValue("selectedRoles", []);
    }
    handleCloseStatus();
  };

  const getSelectedRoleTypes = () => {
    if (!Array.isArray(values.selectedRoles)) return [];
    return values.selectedRoles
      .map((id) => {
        const roleObj = roles.find((r) => r.id.toString() === id);
        return roleObj ? getDisplayRoleName(roleObj.name) : null;
      })
      .filter(Boolean);
  };

  const handleSelectAllRoles = () => {
    // Single-select mode: no "select all" behavior. Keep as no-op to
    // prevent multiple selections when CommonSelect renders a select-all option.
    return;
  };

  const handleTypeToggleForRoles = (val) => {
    // Enforce single selection: set selectedRoles to the chosen role only.
    const match = roles.find((r) => getDisplayRoleName(r.name) === val);
    if (!match) return;
    const idStr = match.id.toString();
    setFieldValue("selectedRoles", [idStr]);
    // Close the role dropdown after selecting a role
    handleCloseRole();
  };

  const handleDepartmentDelete = (deptId) => {
    setFieldValue("participatingDepartments", (currentDepartments) => {
      if (!Array.isArray(currentDepartments)) return [];
      return currentDepartments.filter((d) => d.id !== deptId);
    });
  };

  return (
    <>
      <Form>
        <div className="assign-role-dialog-content">
          <div className="assign-role-dialog-row">
            <div className="form-group">
              <TextInputComponent
                type="text"
                fieldName="username"
                label="Username"
                value={values.username}
                disabled
              />
            </div>
            <div className="form-group">
              <TextInputComponent
                type="text"
                fieldName="email"
                label="Email"
                value={values.email}
                disabled
              />
            </div>
            <div className="form-group">
              <TextInputComponent
                type="text"
                fieldName="department"
                label="Department Name"
                value={values.department}
                disabled
              />
            </div>
          </div>

          <div className="assign-role-dialog-row-two">
            <div className="form-group form-text-input-component">
              <InputLabel>Status</InputLabel>
              <Box
                onClick={handleOpenStatus}
                sx={{
                  border: "1px solid #e8e8ee99",
                  borderRadius: "5px",
                  backgroundColor: "#fff",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: values.status === "Enable" ? "green" : "red",
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  minHeight: "40px",
                  boxShadow: "0 0 0 1px transparent",
                  "&:hover": { boxShadow: "0 0 0 1px #403C6F" },
                }}
              >
                <span>{values.status || "Select Status"}</span>
                <ExpandMoreIcon sx={{ ml: 1, color: "#403C6F" }} />
              </Box>
              <CommonSelect
                id="status-select"
                open={statusOpen}
                anchorEl={statusAnchorEl}
                onClose={handleCloseStatus}
                noteTypes={[values.status === "Enable" ? "Disable" : "Enable"]}
                selectedTypes={[]}
                handleSelectAll={() => {}}
                handleTypeToggle={handleTypeToggleForStatus}
                multiple={false}
                width={345}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              />
              <ErrorMessage
                name="status"
                component="div"
                className="error-message"
              />
            </div>

            <div className="form-group form-text-input-component">
              <InputLabel>Role</InputLabel>
              <Box
                onClick={handleOpenRole}
                sx={getRoleBoxStyles(values.status === "Disable")}
              >
                <span>{getRoleDisplayText(values.selectedRoles, roles)}</span>
                <ExpandMoreIcon sx={{ ml: 1, color: "#403C6F" }} />
              </Box>
              <CommonSelect
                id="role-select"
                open={roleOpen}
                anchorEl={roleAnchorEl}
                onClose={handleCloseRole}
                noteTypes={roleNameList}
                maxHeight={isMobile ? 200 : null}
                selectedTypes={getSelectedRoleTypes()}
                handleSelectAll={handleSelectAllRoles}
                handleTypeToggle={handleTypeToggleForRoles}
                multiple={false}
                width={348}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              />
              {values.status !== "Disable" && (
                <ErrorMessage
                  name="selectedRoles"
                  component="div"
                  className="error-message"
                />
              )}
            </div>
          </div>
        </div>

        <DialogActions>
          <div className="action-btn-wrapper" style={{ gap: "10px" }}>
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="action-btn"
              disabled={!dirty || !isValid}
              style={{
                opacity: !dirty || !isValid ? 0.6 : 1,
                cursor: !dirty || !isValid ? "not-allowed" : "pointer",
              }}
            >
              Save
            </button>
          </div>
        </DialogActions>
      </Form>
      <Dialog
        open={showAllDepartments}
        onClose={() => setShowAllDepartments(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Selected Departments
          <CloseIcon
            onClick={() => setShowAllDepartments(false)}
            style={{ cursor: "pointer", float: "right" }}
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {Array.isArray(values.participatingDepartments) &&
              values.participatingDepartments.map((departmentItem) => (
                <DepartmentChip
                  key={departmentItem.id}
                  dept={departmentItem}
                  departmentsList={mainDepartments}
                  onDelete={() => handleDepartmentDelete(departmentItem.id)}
                />
              ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

AssignRoleFormContent.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  resetForm: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  roles: PropTypes.array.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  mainDepartments: PropTypes.array.isRequired,
  roleNameList: PropTypes.array.isRequired,
  isMobile: PropTypes.bool.isRequired,
  type: PropTypes.string,
};

export default AssignRoleDialog;
